import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import Edit from '../Icons/Edit';
import PanToolAlt from '../Icons/PanToolAlt';
import TapAndPlay from '../Icons/TapAndPlay';
import Mic from '../Icons/Mic';
import Board from '../Icons/Board';
import Video from '../Icons/Video';
import Zoom from '../Icons/Zoom';
import Chat from '../Icons/Chat';
import Light from '../Icons/Light';
import Pause from '../Icons/Pause';
import Photo from '../Icons/Photo';
import Color from '../../styles/common/Color';
import ColorSF2 from '../../styles/common/ColorSF2';

const BaseButton = styled.div`
  color: ${props =>
    (!props.enabled && Color.disabled) || (props.active && '#405289')};
  background-color: ${props =>
    props.enabled && props.active && ColorSF2.skyBlue};
  width: 100%;
  height: 50px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-size: 15px;
  cursor: ${props => (props.enabled ? 'pointer' : 'default')};
`;
const ButtonLabel = styled.div`
  font-size: 10px;
  @media (max-height: 470px) {
    display: none;
  }
`;

/**
 * アイコンとラベルのセットを表示するコンポーネント
 * - propsでアイコンとラベルを切り替える
 * - アクティブまたは非アクティブを切り替えることができる
 *
 * @component
 * @param {Object} props
 * @param {string} props.type - ボタンの種類（例: 'paint', 'mic' など）
 * @param {string} props.label - ボタンのラベルを指定
 * @param {boolean} props.active - ボタンがアクティブかどうかを示すフラグ
 * @param {boolean} [props.enabled=true] - ボタンが有効かどうかを示すフラグ（省略可能、デフォルトはtrue）
 * @param {function} props.onClick - ボタンがクリックされたときのコールバック関数
 * @returns {ReactElement} ControleSwitchButton コンポーネント
 */
const ControleSwitchButton = ({ type, label, active, enabled, onClick }) => {
  const color =
    (!enabled && Color.disabled) ||
    (active && ColorSF2.navyBlue) ||
    ColorSF2.navyBlue;
  return (
    <BaseButton
      className={enabled && 'button'}
      id="togglePaintButton"
      role="presentation"
      onClick={(enabled && onClick) || undefined}
      enabled={enabled}
      active={active}
    >
      {type === 'paint' && <Edit color={color} />}
      {type === 'sticker' && <PanToolAlt color={color} />}
      {type === 'remote' && <TapAndPlay color={color} />}
      {type === 'mic' && <Mic color={color} />}
      {type === 'board' && <Board color={color} />}
      {type === 'video' && <Video color={color} />}
      {type === 'zoom' && <Zoom color={color} />}
      {type === 'chat' && <Chat color={color} />}
      {type === 'light' && <Light color={color} />}
      {type === 'pause' && <Pause color={color} />}
      {type === 'photo' && <Photo color={color} />}
      <ButtonLabel>
        <FormattedMessage id={label} />
      </ButtonLabel>
    </BaseButton>
  );
};

ControleSwitchButton.propTypes = {
  type: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  active: PropTypes.bool.isRequired,
  enabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};
ControleSwitchButton.defaultProps = {
  enabled: true,
};

export default ControleSwitchButton;
